import { getRules } from './Utils';
import getGeometryStyles from './GeometryStyles';
import { processExternalGraphicSymbolizers } from './imageCache';
import { defaultPointStyle } from './styles/static';
import getPointStyle from './styles/pointStyle';
import getLineStyle from './styles/lineStyle';
import getPolygonStyle from './styles/polygonStyle';
import getTextStyle from './styles/textStyle';
import getLinePointStyle from './styles/linePointStyle';
import getPolygonPointStyle from './styles/polygonPointStyle';
import { getScaleForResolution } from '../../utils'
import { EDIT_STYLE_TYPE, EDIT_INTERACTION_STYLE_TYPE } from '../styleTypes';

const defaultStyles = [defaultPointStyle];

/**
 * @private
 * A point symbolizer stamps the mark on every vertex of a line/polygon.
 * Keep that only while editing, so vertices are easy to spot and drag.
 * @param {ol/Feature|ol/geom/Geometry} featureOrGeometry Feature (or geometry, for GeometryCollection members).
 * @returns {boolean} True when the feature is in edit state.
 */
function showVertexMarkers(featureOrGeometry) {
    if (!featureOrGeometry || typeof featureOrGeometry.get !== 'function') {
        return false;
    }

    const xstate = Number(featureOrGeometry.get('xstate'));
    return xstate === EDIT_STYLE_TYPE || xstate === EDIT_INTERACTION_STYLE_TYPE;
}

/**
 * @private
 * Convert symbolizers together with the feature to OL style objects and append them to the styles array.
 * @example appendStyle(styles, point[j], feature, getPointStyle);
 * @param {Array<ol/style>} styles Array of OL styles.
 * @param {object|Array<object>} symbolizers Feature symbolizer object, or array of feature symbolizers.
 * @param {ol/feature} feature OpenLayers feature.
 * @param {Function} styleFunction Function for getting the OL style object. Signature (symbolizer, feature) => OL style.
 * @param {Function} getProperty A property getter: (feature, propertyName) => property value.
 */
function appendStyle(styles, symbolizers, feature, styleFunction, getProperty, isCollection, zIndex) {
    if (Array.isArray(symbolizers)) {
        for (let k = 0; k < symbolizers.length; k += 1) {
            let styleObj = null;

            if (isCollection) {
                styleObj = styleFunction(symbolizers[k], feature, getProperty).clone();
                feature.getGeometry = () => { return feature }
                styleObj.geometryFunction_ = () => { return feature }
            } else {
                styleObj = styleFunction(symbolizers[k], feature, getProperty);
            }

            if (symbolizers.zIndex && styleObj.setZIndex) {
                styleObj.setZIndex(symbolizers.zIndex);
            }

            if (symbolizers[k].zIndex && styleObj.setZIndex) {
                styleObj.setZIndex(symbolizers[k].zIndex);
            }

            if (zIndex) {
                styleObj.setZIndex(zIndex);
            }

            styles.push(styleObj);
        }
    } else {
        let styleObj = null;

        if (isCollection) {
            styleObj = styleFunction(symbolizers, feature, getProperty).clone();
            feature.getGeometry = () => { return feature }
            styleObj.geometryFunction_ = () => { return feature }
        } else {
            styleObj = styleFunction(symbolizers, feature, getProperty);
        }

        if (symbolizers.zIndex && styleObj.setZIndex) {
            styleObj.setZIndex(symbolizers.zIndex);
        }

        if (zIndex) {
            styleObj.setZIndex(zIndex);
        }

        styles.push(styleObj);
    }
}

/**
 * Create openlayers style
 * @example OlStyler(getGeometryStyles(rules), geojson.geometry.type);
 * @param {GeometryStyles} GeometryStyles rulesconverter
 * @param {object|Feature} feature {@link http://geojson.org|geojson}
 *  or {@link https://openlayers.org/en/latest/apidoc/module-ol_Feature-Feature.html|ol/Feature} Changed in 0.0.04 & 0.0.5!
 * @param {Function} getProperty A property getter: (feature, propertyName) => property value.
 * @return ol.style.Style or array of it
 */
export default function OlStyler(GeometryStyles, feature, getProperty) {
    const { polygon, line, point, text } = GeometryStyles;

    if (feature.get('features') != null) {
        feature.set('size', feature.get('features').length);
    }

    const geometry = feature.getGeometry
        ? feature.getGeometry()
        : feature.geometry;
    const geometryType = geometry.getType ? geometry.getType() : geometry.type;

    let styles = [];
    switch (geometryType) {
        case 'Point':
        case 'MultiPoint':
            for (let j = 0; j < point.length; j += 1) {
                appendStyle(styles, point[j], feature, getPointStyle, getProperty);
            }
            for (let j = 0; j < text.length; j += 1) {
                appendStyle(styles, text[j], feature, getTextStyle, getProperty, false, 100);
            }
            break;

        case 'LineString':
        case 'MultiLineString':
            for (let j = 0; j < line.length; j += 1) {
                appendStyle(styles, line[j], feature, getLineStyle, getProperty);
            }
            if (showVertexMarkers(feature)) {
                for (let j = 0; j < point.length; j += 1) {
                    appendStyle(styles, point[j], feature, getLinePointStyle, getProperty);
                }
            }
            for (let j = 0; j < text.length; j += 1) {
                appendStyle(styles, text[j], feature, getTextStyle, getProperty, false, 100);
            }
            break;
        case 'Polygon':
        case 'MultiPolygon':
            for (let j = 0; j < polygon.length; j += 1) {
                appendStyle(styles, polygon[j], feature, getPolygonStyle, getProperty);
            }
            for (let j = 0; j < line.length; j += 1) {
                appendStyle(styles, line[j], feature, getLineStyle, getProperty);
            }
            if (showVertexMarkers(feature)) {
                for (let j = 0; j < point.length; j += 1) {
                    appendStyle(
                        styles,
                        point[j],
                        feature,
                        getPolygonPointStyle,
                        getProperty
                    );
                }
            }
            for (let j = 0; j < text.length; j += 1) {
                appendStyle(styles, text[j], feature, getTextStyle, getProperty, false, 100);
            }
            break;
        case 'Circle':
            for (let j = 0; j < polygon.length; j += 1) {
                appendStyle(styles, polygon[j], feature, getPolygonStyle, getProperty);
            }
            break;
        case 'GeometryCollection':
            let geometries = feature.getGeometry().getGeometries();
            setAttributes(geometries, feature);
            handleGeometryCollection(geometries, styles, GeometryStyles, getProperty);
            break;
        default:
            styles = defaultStyles;
    }

    // Set z-index of styles explicitly to fix a bug where GraphicStroke is always rendered above a line symbolizer.
    //styles.forEach((style, index) => style.setZIndex(index));

    return styles;
}

function setAttributes(geoms, feat) {
    let props = feat.getProperties();
    let propKeys = Object.keys(props);

    geoms.forEach(geom => {
        propKeys.forEach(x => {
            if (x != 'geometry') {
                geom.set(x, feat.get(x));
            }
        });
    });
}

function handleGeometryCollection(geometries, styles, GeometryStyles, getProperty) {
    let { polygon, line, point, text } = GeometryStyles;
    geometries.forEach(geometry => {
        let geomType = geometry.getType();
        switch (geomType) {
            case 'Point':
            case 'MultiPoint':
                for (let j = 0; j < point.length; j += 1) {
                    appendStyle(styles, point[j], geometry, getPointStyle, getProperty, true);
                }
                for (let j = 0; j < text.length; j += 1) {
                    appendStyle(styles, text[j], geometry, getTextStyle, getProperty, true, 100);
                }
                break;

            case 'LineString':
            case 'MultiLineString':
                for (let j = 0; j < line.length; j += 1) {
                    appendStyle(styles, line[j], geometry, getLineStyle, getProperty, true);
                }
                if (showVertexMarkers(geometry)) {
                    for (let j = 0; j < point.length; j += 1) {
                        appendStyle(styles, point[j], geometry, getLinePointStyle, getProperty, true);
                    }
                }
                for (let j = 0; j < text.length; j += 1) {
                    appendStyle(styles, text[j], geometry, getTextStyle, getProperty, true, 100);
                }
                break;

            case 'Polygon':
            case 'MultiPolygon':
                for (let j = 0; j < polygon.length; j += 1) {
                    appendStyle(styles, polygon[j], geometry, getPolygonStyle, getProperty, true);
                }
                for (let j = 0; j < line.length; j += 1) {
                    appendStyle(styles, line[j], geometry, getLineStyle, getProperty, true);
                }
                if (showVertexMarkers(geometry)) {
                    for (let j = 0; j < point.length; j += 1) {
                        appendStyle(
                            styles,
                            point[j],
                            geometry,
                            getPolygonPointStyle,
                            getProperty, true
                        );
                    }
                }
                for (let j = 0; j < text.length; j += 1) {
                    appendStyle(styles, text[j], geometry, getTextStyle, getProperty, true, 100);
                }
                break;
        }
    });
}

/**
 * @private
 * Extract feature id from an OpenLayers Feature.
 * @param {Feature} feature {@link https://openlayers.org/en/latest/apidoc/module-ol_Feature-Feature.html|ol/Feature}
 * @returns {string} Feature id.
 */
function getOlFeatureId(feature) {
    return feature.getId();
}

/**
 * @private
 * Extract a property value from an OpenLayers Feature.
 * @param {Feature} feature {@link https://openlayers.org/en/latest/apidoc/module-ol_Feature-Feature.html|ol/Feature}
 * @param {string} propertyName The name of the feature property to read.
 * @returns {object} Property value.
 */
function getOlFeatureProperty(feature, propertyName) {
    return feature.get(propertyName);
}

function getRenderSimplificationState(options) {
    const renderSimplification = options && options.renderSimplification;
    if (!renderSimplification || renderSimplification.enabled !== true) {
        return null;
    }

    const toleranceFactor = Number(renderSimplification.toleranceFactor);
    const bucketStepsPerZoom = Number(renderSimplification.bucketStepsPerZoom);
    const maxBucketsPerGeometry = Number(renderSimplification.maxBucketsPerGeometry);
    const minVertexCount = Number(renderSimplification.minVertexCount);
    const avoidComputeWhileInteracting = renderSimplification.avoidComputeWhileInteracting !== false;
    const reuseNearestBucketWhileInteracting = renderSimplification.reuseNearestBucketWhileInteracting !== false;

    return {
        toleranceFactor: Number.isFinite(toleranceFactor) && toleranceFactor > 0 ? toleranceFactor : 1,
        bucketStepsPerZoom: Number.isFinite(bucketStepsPerZoom) && bucketStepsPerZoom > 0 ? bucketStepsPerZoom : 4,
        maxBucketsPerGeometry: Number.isFinite(maxBucketsPerGeometry) && maxBucketsPerGeometry > 0 ? maxBucketsPerGeometry : 8,
        minVertexCount: Number.isFinite(minVertexCount) && minVertexCount > 1 ? Math.floor(minVertexCount) : 64,
        avoidComputeWhileInteracting: avoidComputeWhileInteracting,
        reuseNearestBucketWhileInteracting: reuseNearestBucketWhileInteracting,
        geometryCache: new WeakMap(),
        currentResolution: null,
        currentIsInteracting: false
    };
}

function getRenderCullingState(options) {
    const renderCulling = options && options.renderCulling;
    if (!renderCulling || renderCulling.enabled !== true) {
        return null;
    }

    const minLineLengthPx = Number(renderCulling.minLineLengthPx);
    const minPolygonAreaPx2 = Number(renderCulling.minPolygonAreaPx2);
    const minPolygonExtentPx = Number(renderCulling.minPolygonExtentPx);

    return {
        minLineLengthPx: Number.isFinite(minLineLengthPx) && minLineLengthPx > 0 ? minLineLengthPx : 3.5,
        minPolygonAreaPx2: Number.isFinite(minPolygonAreaPx2) && minPolygonAreaPx2 > 0 ? minPolygonAreaPx2 : 10,
        minPolygonExtentPx: Number.isFinite(minPolygonExtentPx) && minPolygonExtentPx > 0 ? minPolygonExtentPx : 3.75,
        currentResolution: null
    };
}

function getResolutionBucket(resolution, bucketStepsPerZoom) {
    if (!Number.isFinite(resolution) || resolution <= 0) {
        return null;
    }

    return Math.round(Math.log2(resolution) * bucketStepsPerZoom);
}

function isLineOrPolygonType(geometryType) {
    return geometryType === 'LineString' ||
        geometryType === 'MultiLineString' ||
        geometryType === 'Polygon' ||
        geometryType === 'MultiPolygon';
}

function getGeometryExtentPixelSize(geometry, resolution) {
    if (!geometry || typeof geometry.getExtent !== 'function') {
        return { widthPx: null, heightPx: null };
    }

    const extent = geometry.getExtent();
    if (!extent || extent.length < 4 || !Number.isFinite(resolution) || resolution <= 0) {
        return { widthPx: null, heightPx: null };
    }

    return {
        widthPx: Math.abs((extent[2] - extent[0]) / resolution),
        heightPx: Math.abs((extent[3] - extent[1]) / resolution)
    };
}

function shouldCullGeometryByType(geometry, cullingState, resolution) {
    if (!geometry || !cullingState || !Number.isFinite(resolution) || resolution <= 0 || typeof geometry.getType !== 'function') {
        return false;
    }

    const geometryType = geometry.getType();

    if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
        let lineLength = null;
        if (typeof geometry.getLength === 'function') {
            lineLength = geometry.getLength();
        }

        if (!Number.isFinite(lineLength)) {
            const extentSize = getGeometryExtentPixelSize(geometry, resolution);
            if (Number.isFinite(extentSize.widthPx) && Number.isFinite(extentSize.heightPx)) {
                lineLength = Math.sqrt(extentSize.widthPx * extentSize.widthPx + extentSize.heightPx * extentSize.heightPx) * resolution;
            }
        }

        if (!Number.isFinite(lineLength)) {
            return false;
        }

        const lineLengthPx = lineLength / resolution;
        return lineLengthPx < cullingState.minLineLengthPx;
    }

    if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
        if (typeof geometry.getArea !== 'function') {
            return false;
        }

        const area = Math.abs(geometry.getArea());
        if (!Number.isFinite(area)) {
            return false;
        }

        const areaPx2 = area / (resolution * resolution);
        const extentSize = getGeometryExtentPixelSize(geometry, resolution);
        const maxExtentPx = Number.isFinite(extentSize.widthPx) && Number.isFinite(extentSize.heightPx)
            ? Math.max(extentSize.widthPx, extentSize.heightPx)
            : null;

        return areaPx2 < cullingState.minPolygonAreaPx2 &&
            Number.isFinite(maxExtentPx) &&
            maxExtentPx < cullingState.minPolygonExtentPx;
    }

    if (geometryType === 'GeometryCollection' && typeof geometry.getGeometries === 'function') {
        const geometries = geometry.getGeometries();
        if (!Array.isArray(geometries) || geometries.length === 0) {
            return false;
        }

        // Cull collection only when all child geometries are too small to render.
        return geometries.every(child => shouldCullGeometryByType(child, cullingState, resolution));
    }

    return false;
}

function shouldCullFeatureForRender(feature, cullingState) {
    if (!feature || !cullingState || typeof feature.get !== 'function') {
        return false;
    }

    if (feature.get('skipRenderCulling') === true) {
        return false;
    }

    const xstate = feature.get('xstate');
    if (xstate != null && xstate !== 0 && xstate !== '0') {
        // Keep interacted/edited/selected features visible regardless of culling.
        return false;
    }

    const resolution = Number(cullingState.currentResolution);
    if (!Number.isFinite(resolution) || resolution <= 0) {
        return false;
    }

    const geometry = feature.getGeometry ? feature.getGeometry() : feature.geometry;
    return shouldCullGeometryByType(geometry, cullingState, resolution);
}

function setRenderCulledFlag(feature, isCulled) {
    if (!feature || typeof feature.get !== 'function' || typeof feature.set !== 'function') {
        return;
    }

    const currentValue = feature.get('xrenderCulled') === true;
    const nextValue = isCulled === true;
    if (currentValue !== nextValue) {
        feature.set('xrenderCulled', nextValue, true);
    }
}

function simplifyGeometryForRender(geometry, feature, simplifyState) {
    if (!simplifyState || !geometry || typeof geometry.getType !== 'function') {
        return geometry;
    }

    const geometryType = geometry.getType();
    if (!isLineOrPolygonType(geometryType) || typeof geometry.simplify !== 'function') {
        return geometry;
    }

    if (typeof geometry.getFlatCoordinates === 'function') {
        const flatCoordinates = geometry.getFlatCoordinates();
        const stride = typeof geometry.getStride === 'function' ? geometry.getStride() : 2;
        if (Array.isArray(flatCoordinates) && stride > 0) {
            const vertexCount = Math.floor(flatCoordinates.length / stride);
            if (vertexCount < simplifyState.minVertexCount) {
                return geometry;
            }
        }
    }

    let resolution = simplifyState.currentResolution;
    if (feature && typeof feature.get === 'function') {
        const featureResolution = Number(feature.get('currentResolution'));
        if (Number.isFinite(featureResolution) && featureResolution > 0) {
            resolution = featureResolution;
        }
    }

    if (!Number.isFinite(resolution) || resolution <= 0) {
        return geometry;
    }

    const bucketKey = getResolutionBucket(resolution, simplifyState.bucketStepsPerZoom);
    if (bucketKey == null) {
        return geometry;
    }

    let cacheEntry = simplifyState.geometryCache.get(geometry);
    const geometryRevision = typeof geometry.getRevision === 'function' ? geometry.getRevision() : 0;

    if (!cacheEntry) {
        cacheEntry = {
            revision: geometryRevision,
            buckets: new Map()
        };
        simplifyState.geometryCache.set(geometry, cacheEntry);
    } else if (cacheEntry.revision !== geometryRevision) {
        cacheEntry.revision = geometryRevision;
        cacheEntry.buckets.clear();
    }

    if (cacheEntry.buckets.has(bucketKey)) {
        return cacheEntry.buckets.get(bucketKey);
    }

    if (simplifyState.currentIsInteracting && simplifyState.avoidComputeWhileInteracting) {
        if (simplifyState.reuseNearestBucketWhileInteracting && cacheEntry.buckets.size > 0) {
            let nearestBucketGeometry = null;
            let nearestBucketDistance = Infinity;

            for (const [cachedBucketKey, cachedGeometry] of cacheEntry.buckets) {
                const distance = Math.abs(cachedBucketKey - bucketKey);
                if (distance < nearestBucketDistance) {
                    nearestBucketDistance = distance;
                    nearestBucketGeometry = cachedGeometry;
                }
            }

            if (nearestBucketGeometry) {
                return nearestBucketGeometry;
            }
        }

        return geometry;
    }

    const tolerance = resolution * simplifyState.toleranceFactor;
    if (!Number.isFinite(tolerance) || tolerance <= 0) {
        return geometry;
    }

    const simplifiedGeometry = geometry.simplify(tolerance);
    const renderGeometry = simplifiedGeometry || geometry;

    cacheEntry.buckets.set(bucketKey, renderGeometry);
    if (cacheEntry.buckets.size > simplifyState.maxBucketsPerGeometry) {
        const firstKey = cacheEntry.buckets.keys().next().value;
        cacheEntry.buckets.delete(firstKey);
    }

    return renderGeometry;
}

function wrapStyleGeometryForRenderSimplification(style, simplifyState) {
    if (!style || !simplifyState || style.__xportalRenderSimplificationWrapped === true) {
        return;
    }

    if (typeof style.getGeometryFunction !== 'function' || typeof style.setGeometry !== 'function') {
        return;
    }

    const baseGeometryFunction = style.getGeometryFunction();
    if (typeof baseGeometryFunction !== 'function') {
        return;
    }

    style.setGeometry(feature => {
        const geometry = baseGeometryFunction(feature);
        return simplifyGeometryForRender(geometry, feature, simplifyState);
    });

    style.__xportalRenderSimplificationWrapped = true;
}

function applyRenderSimplificationToStyles(styleValue, simplifyState) {
    if (!simplifyState || styleValue == null) {
        return;
    }

    if (Array.isArray(styleValue)) {
        styleValue.forEach(style => wrapStyleGeometryForRenderSimplification(style, simplifyState));
        return;
    }

    wrapStyleGeometryForRenderSimplification(styleValue, simplifyState);
}

/**
 * Create an OpenLayers style function from a FeatureTypeStyle object extracted from an SLD document.
 *
 * **Important!** When using externalGraphics for point styling, make sure to call .changed() on the layer
 * inside options.imageLoadedCallback to immediately see the loaded image. If you do not do this, the
 * image icon will only become visible the next time OpenLayers draws the layer (after pan or zoom).
 * @param {FeatureTypeStyle} featureTypeStyle Feature Type Style object.
 * @param {object} options Options
 * @param {function} options.convertResolution An optional function to convert the resolution in map units/pixel to resolution in meters/pixel.
 * When not given, the map resolution is used as-is.
 * @param {function} options.imageLoadedCallback Optional callback that will be called with the url of an externalGraphic when
 * an image has been loaded (successfully or not). Call .changed() inside the callback on the layer to see the loaded image.
 * @param {function} options.getProperty Optional custom property getter: (feature, propertyName) => property value.
 * @returns {Function} A function that can be set as style function on an OpenLayers vector style layer.
 * @example
 * myOlVectorLayer.setStyle(SLDReader.createOlStyleFunction(featureTypeStyle, {
 *   imageLoadedCallback: () => { myOlVectorLayer.changed(); }
 * }));
 */
export function createOlStyleFunction(featureTypeStyle, map, options = {}) {
    const imageLoadedCallback = options.imageLoadedCallback || null;
    const simplifyState = getRenderSimplificationState(options);
    const cullingState = getRenderCullingState(options);

    // Keep track of whether a callback has been registered per image url.
    const callbackRef = {};
    const layerByIdCache = new Map();
    const layerChangedCallbackCache = new Map();
    const emptyCallback = () => { };

    const currentMap = map;
    return (feature, mapResolution) => {
        const currentView = currentMap.getView();

        // Determine resolution in meters/pixel.
        let currentResolution = currentView.getResolution();
        const currentProjection = currentView.getProjection();
        const currentScale = getScaleForResolution(currentResolution, currentProjection.getCode());
        let currentUom = currentProjection.getUnits();
        if (simplifyState) {
            simplifyState.currentResolution = currentResolution;
            const isInteracting = (typeof currentView.getInteracting === 'function' && currentView.getInteracting()) ||
                (typeof currentView.getAnimating === 'function' && currentView.getAnimating());
            simplifyState.currentIsInteracting = Boolean(isInteracting);
        }
        if (cullingState) {
            cullingState.currentResolution = currentResolution;
            const isCulled = shouldCullFeatureForRender(feature, cullingState);
            setRenderCulledFlag(feature, isCulled);
            if (isCulled) {
                return [];
            }
        } else {
            setRenderCulledFlag(feature, false);
        }

        if (feature.get('currentResolution') !== currentResolution) {
            feature.set('currentResolution', currentResolution, true);
        }

        if (feature.get('currentUom') !== currentUom) {
            feature.set('currentUom', currentUom, true);
        }

        const getProperty =
            typeof options.getProperty === 'function'
                ? options.getProperty
                : getOlFeatureProperty;

        // Determine applicable style rules for the feature, taking feature properties and current resolution into account.
        const rules = getRules(featureTypeStyle, feature, currentScale, {
            getProperty,
            getFeatureId: getOlFeatureId,
        });

        let resolvedImageLoadedCallback = imageLoadedCallback;
        if (resolvedImageLoadedCallback == null) {
            const layerId = feature.layerId != null
                ? feature.layerId
                : (feature.get('features') != null && feature.get('features').length > 0)
                    ? feature.get('features')[0].layerId
                    : null;

            if (layerId != null) {
                if (!layerChangedCallbackCache.has(layerId)) {
                    let layer = layerByIdCache.get(layerId);
                    if (layer === undefined) {
                        const layers = currentMap.getLayers().getArray();
                        layer = layers.find(l => l.get("uid") == layerId) || null;
                        layerByIdCache.set(layerId, layer);
                    }

                    if (layer != null) {
                        layerChangedCallbackCache.set(layerId, () => {
                            const source = layer.getSource();
                            if (source) {
                                source.changed();
                            }
                        });
                    } else {
                        layerChangedCallbackCache.set(layerId, emptyCallback);
                    }
                }

                resolvedImageLoadedCallback = layerChangedCallbackCache.get(layerId);
            }
        }

        if (resolvedImageLoadedCallback == null) {
            resolvedImageLoadedCallback = emptyCallback;
        }

        // Start loading images for external graphic symbolizers and when loaded:
        // * update symbolizers to use the cached image.
        // * call imageLoadedCallback with the image url.
        processExternalGraphicSymbolizers(
            feature,
            rules,
            featureTypeStyle,
            resolvedImageLoadedCallback,
            callbackRef
        );

        // Convert style rules to style rule lookup categorized by geometry type.
        const geometryStyles = getGeometryStyles(rules);

        // Determine style rule array.
        const olStyles = OlStyler(geometryStyles, feature, getProperty);
        applyRenderSimplificationToStyles(olStyles, simplifyState);

        return olStyles;
    };
}
